import type { Language } from '../i18n';
import { catalog, catalogVersion, projectAreaIds } from './project-pricing.ts';
import advisorOptions from '../../shared/advisor-options.json' with { type: 'json' };

export type Advice = { areas: string[]; recommended: string[]; optional: string[]; customQuote: boolean; catalogVersion: string; platforms: string[]; website: string };
export function parseAdvice(value: unknown): Advice {
  if (!value || typeof value !== 'object') throw new Error('invalid_advice');
  const advice = value as Advice;
  // Older deployed responses remain usable during the frontend/backend rollout.
  const platforms = advice.platforms ?? [], website = advice.website ?? '';
  if (!Array.isArray(platforms) || platforms.length > 6 || platforms.some(id => !advisorOptions.platforms.includes(id)) || !['', ...advisorOptions.websites].includes(website)) throw new Error('invalid_advice');
  if (advice.catalogVersion !== catalogVersion || typeof advice.customQuote !== 'boolean') throw new Error('invalid_advice');
  for (const [field, max] of [['areas', 5], ['recommended', 5], ['optional', 3]] as const) {
    if (!Array.isArray(advice[field]) || advice[field].length > max || advice[field].some(id => typeof id !== 'string' || !(field === 'areas' ? projectAreaIds.some(area => area === id) : catalog.some(item => item.id === id)))) throw new Error('invalid_advice');
  }
  return { ...advice, areas: [...new Set(advice.areas)], recommended: [...new Set(advice.recommended)], optional: [...new Set(advice.optional)].filter(id => !advice.recommended.includes(id)), platforms: [...new Set(platforms)], website };
}

export async function requestProjectAdvice(description: string, language: Language, signal: AbortSignal, consent: boolean, fetcher: typeof fetch = fetch) {
  if (consent !== true) throw new Error('consent_required');
  const response = await fetcher('/api/project-advisor', { method: 'POST', headers: { 'Content-Type': 'application/json' }, signal, body: JSON.stringify({ description: description.trim(), language, consent: true }) });
  if (!response.ok) {
    let code = 'failed';
    try { const data = await response.json(); if (data.error === 'personal_data') code = 'personal'; else if (response.status === 422 && data.error === 'off_topic') code = 'off_topic'; } catch { /* Never show provider responses to the visitor. */ }
    if (response.status === 429) code = 'limited';
    if (response.status === 503) code = 'unavailable';
    throw new Error(code);
  }
  return parseAdvice(await response.json());
}
